import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ClearOutlined from "@mui/icons-material/ClearOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS, QUDA_COLORS, DOCLINK_COLORS, MEDIDROP_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

// Pastel background + accent per app, so each group is visually tied to its app colours.
// (Zephyr has no brand colour in the palette — a soft indigo is used for it.)
const APP_STYLE = {
  Quda:     { bg: "#e6f6f6", border: "#bfe6e6", accent: QUDA_COLORS.TEAL_DARK },
  Zephyr:   { bg: "#eef1fb", border: "#d7ddf6", accent: "#3949ab" },
  DocLink:  { bg: DOCLINK_COLORS.LAVENDER_TINT, border: "#e6d6f5", accent: DOCLINK_COLORS.MID_PURPLE },
  MediDrop: { bg: MEDIDROP_COLORS.ORANGE_TINT, border: "#f6d8c8", accent: MEDIDROP_COLORS.ORANGE_DARK },
};
const DEFAULT_APP_STYLE = { bg: "#f8fafc", border: "#e5e7eb", accent: BLUE };

// Grouped field definitions — mirrors the columns on tblPharmacyHaloSettings
// that drive the global header / warning banners shown inside the desktop apps.
const GROUPS = [
  {
    label: "Quda",
    fields: [
      { key: "globalWarning_Hub", label: "Global warning", helper: "Banner message shown across Quda.", multiline: true },
      { key: "globalWarningButtonLink_Hub", label: "Global warning button link", helper: "URL opened when the Quda banner button is clicked." },
    ],
  },
  {
    label: "Zephyr",
    fields: [
      { key: "ZephyrGlobalHeader", label: "Global header", helper: "Banner message shown across Zephyr.", multiline: true },
    ],
  },
  {
    label: "DocLink",
    fields: [
      { key: "DocLinkGlobalHeader", label: "Global header (app-wide)", helper: "Banner message shown across all of DocLink.", multiline: true },
      { key: "GlobalHeader_Medmate", label: "Medmate header", helper: "Banner message shown on the Medmate integration screens.", multiline: true },
      { key: "GlobalHeader_Hubmed", label: "HubMed header", helper: "Banner message shown on the HubMed integration screens.", multiline: true },
      { key: "GlobalHeader_Hola", label: "Hola header", helper: "Banner message shown on the Hola integration screens.", multiline: true },
    ],
  },
  {
    label: "MediDrop",
    fields: [
      { key: "GlobalHeader_MediDrop", label: "Global header", helper: "Banner / dashboard notification shown across MediDrop.", multiline: true },
    ],
  },
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields.map((f) => f.key));

const emptyValues = () => Object.fromEntries(ALL_FIELDS.map((k) => [k, ""]));

const GlobalHeadersModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(emptyValues());
  const [values, setValues] = useState(emptyValues());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.GLOBAL_HEADERS, "GET");
      const settings = res?.settings ?? {};
      const next = emptyValues();
      ALL_FIELDS.forEach((key) => { next[key] = settings[key] ?? ""; });
      setOriginal(next);
      setValues(next);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load global header settings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    } else {
      setError(null);
      setOriginal(emptyValues());
      setValues(emptyValues());
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleClear = (key) => {
    setValues((prev) => ({ ...prev, [key]: "" }));
  };

  const isDirty = ALL_FIELDS.some((key) => (values[key] || "") !== (original[key] || ""));

  const handleSave = async () => {
    const changed = {};
    ALL_FIELDS.forEach((key) => {
      if ((values[key] || "") !== (original[key] || "")) {
        changed[key] = values[key] || "";
      }
    });
    if (Object.keys(changed).length === 0) return;

    setSaving(true);
    try {
      const res = await phappsRequest(API_PATHS.GLOBAL_HEADERS, "PUT", changed);
      const settings = res?.settings ?? {};
      const next = emptyValues();
      ALL_FIELDS.forEach((key) => { next[key] = settings[key] ?? ""; });
      setOriginal(next);
      setValues(next);
      setSnackbar({ open: true, message: "Global headers updated ✓", severity: "success" });
      onClose();
    } catch (e) {
      setSnackbar({ open: true, message: getAdminApiErrorMessage(e, "Failed to save global headers"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${BLUE}15` }}>
              <CampaignOutlined sx={{ fontSize: 19, color: BLUE }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#1e293b", lineHeight: 1.2 }}>
                Global Headers
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Banner messages shown inside the desktop apps
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close" disabled={saving}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : error ? (
            <Alert severity="warning">{error}</Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {GROUPS.map((group) => {
                const style = APP_STYLE[group.label] || DEFAULT_APP_STYLE;
                return (
                  <Box
                    key={group.label}
                    sx={{
                      backgroundColor: style.bg,
                      border: `1px solid ${style.border}`,
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: style.accent, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.25 }}>
                      {group.label}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {group.fields.map((field) => (
                        <Box key={field.key} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                          <TextField
                            label={field.label}
                            helperText={field.helper}
                            value={values[field.key] ?? ""}
                            onChange={handleChange(field.key)}
                            multiline={!!field.multiline}
                            minRows={field.multiline ? 2 : 1}
                            fullWidth
                            size="small"
                            variant="outlined"
                            // White field on the pastel card so the input area stays clear.
                            sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                          />
                          <Tooltip title="Clear">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleClear(field.key)}
                                disabled={!values[field.key]}
                                sx={{ mt: 0.5 }}
                              >
                                <ClearOutlined sx={{ fontSize: 17 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving || !!error || !isDirty}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: BLUE, "&:hover": { bgcolor: "#001a52" } }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GlobalHeadersModal;
